import type Highcharts from "highcharts";
import React from "react";
import { renderToString } from "react-dom/server";
import { FLAG_AXIS } from "../../../Chart/series/getAnalysisFlagSerie";
import validateInterface from "@ui/lib/validateInterface";
import { PointMetadata, QUANTITY_FLAG_METADATA } from "../../../Chart/PointMetadata";
import type { Options } from "@ui/Highcharts";
import useYMinMax from "@ui/Highcharts/hooks/useYMinMax";
import useAnalysisXAxis from "../../../Chart/hooks/useAnalysisXAxis";
import formatNumber from "@ui/i18n/formatNumber";
import type { NonEmptyArray } from "diagnostics-lib/array";
import type { Project } from "../../../../../api/useProjects";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import type { Source } from "../../../../../api/useSources";
import useNoDataPlotBands from "@ui/Highcharts/hooks/useNoDataPlotBands";
import { useDiagnosticWithQuantityNoDataIntervals } from "../../../useNoDataIntervals";
import type { IndexedFailureValues } from "../../../../../api/useIndexedFailureValues";
import { useTranslations } from "../../../../../api/useTranslations";
import useGroupBreaks from "../../../Chart/hooks/useGroupBreaks";
import { ChartTooltip } from "../../../Chart/ChartTooltip";
import { AnalysisDateChartTooltip } from "../../../Chart/ChartTooltip/common/AnalysisDateChartTooltip";
import { FaultInfoChartTooltip } from "../../../Chart/ChartTooltip/diagnosis/FaultInfoChartTooltip";
import { FaultDescriptionChartTooltip } from "../../../Chart/ChartTooltip/diagnosis/FaultDescriptionChartTooltip";
import { FaultReparabilityChartTooltip } from "../../../Chart/ChartTooltip/diagnosis/FaultReparabilityChartTooltip";
import { FlagChartTooltip } from "../../../Chart/ChartTooltip/common/FlagChartTooltip";
import type { Quantity } from "../../../../../api/useQuantities";
import { isFaultPromoted } from "../../../../../domain/isFaultPromoted";
import { CHART_SELECTION_PLOT_LINE_MARGIN } from "../../../Chart/hooks/useAnalysisSelectionPlotLines";

interface HookOptions {
  source: Source;
  project: Project;
  yAxisId: string;
  chartRef: React.RefObject<Highcharts.Chart>;
  analyses: NonEmptyArray<ActiveAnalysis>;
  indexedFailureValues: IndexedFailureValues;
  series: (
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesArearangeOptions
    | Highcharts.SeriesAreaOptions
    | Highcharts.SeriesScatterOptions
  )[];
  chartTypeOption: { label: string; value: string };
  qoi: Quantity;
}

export const GLOBAL_DIAGNOSIS_CHART = "GLOBAL_DIAGNOSIS_CHART";

const useOptions = ({
  source,
  project,
  yAxisId,
  chartRef,
  analyses,
  indexedFailureValues,
  series,
  chartTypeOption,
  qoi,
}: HookOptions) => {
  const translations = useTranslations();
  const xBreaks = useGroupBreaks(project.id, analyses);
  const initialXAxisOptions = useAnalysisXAxis({
    chartRef,
    project,
    source,
    analyses,
    boundaries: "active-analyses",
  });
  const analysisDateMin = Math.min(...analyses.map(a => a.date));
  const initialYAxisOptions = useYMinMax({
    chartRef,
    yAxisId,
    scope: `${qoi ? `sign-${-1}` : "qoi"}`,
  });

  useNoDataPlotBands({
    chartRef,
    intervals: useDiagnosticWithQuantityNoDataIntervals(analyses, indexedFailureValues),
  });

  const chartTypeOptionLabelRef = React.useRef(chartTypeOption.label);
  chartTypeOptionLabelRef.current = chartTypeOption.label;
  const qoiRef = React.useRef(qoi);
  qoiRef.current = qoi;

  const constantTooltipFormatter = React.useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      if (!this.point.options.custom) return false;
      const metadata = validateInterface(PointMetadata, this.point.options.custom);

      const humanValueParams = "human" in metadata ? metadata.human : null;
      // no tooltip for "link" points
      if (
        (metadata.type === "FAILURE_METADATA" || metadata.type === "MANUAL_FAILURE_METADATA") &&
        !metadata.failureName
      ) {
        return false;
      }

      const humanValue =
        humanValueParams &&
        formatNumber(humanValueParams.value, {
          sign: humanValueParams.sign ?? undefined,
          precision: humanValueParams.precision ?? undefined,
          unit: humanValueParams.unit ?? undefined,
        });

      const reparability = "reparability" in metadata ? metadata.reparability : null;

      const confidence =
        "confidenceIndicator" in metadata ? (metadata.confidenceIndicator ?? null) : null;
      const failureDisplayName =
        "failureDisplayName" in metadata ? metadata.failureDisplayName : null;
      const description = "failureDescription" in metadata ? metadata.failureDescription : null;
      const faultType = "isManual" in metadata && metadata.isManual ? "MANUAL_FAILURE" : "FAILURE";
      const amplitude =
        metadata.type === "FAILURE_METADATA" && metadata.failureAmplitude
          ? (formatNumber(metadata.failureAmplitude, {
              unit: metadata.failureUnit ?? "",
              precision: 2,
            }) ?? null)
          : null;
      const lossValue = humanValue;
      const flags = metadata.type === QUANTITY_FLAG_METADATA ? metadata.flags : undefined;

      const quantityValues = "quantityValues" in metadata ? (metadata.quantityValues ?? {}) : {};
      const filters = "filters" in metadata ? (metadata.filters ?? []) : [];
      const isPromoted = isFaultPromoted(quantityValues, filters);

      return renderToString(
        <ChartTooltip>
          <AnalysisDateChartTooltip
            date={this.point.x}
            group={metadata.group}
            label={metadata.label}
            timezone={project.timezone}
          />
          {this.point.description ? (
            <FaultDescriptionChartTooltip description={this.point.description} />
          ) : null}
          {this.series.name ? (
            <FaultInfoChartTooltip
              type={faultType}
              failureDisplayName={failureDisplayName ?? this.series.name}
              description={description}
              isPromoted={isPromoted}
              confidence={confidence}
              amplitude={amplitude}
              losses={
                qoiRef.current === null || lossValue === null
                  ? null
                  : { value: lossValue, qoi: qoiRef.current["name"] }
              }
            />
          ) : null}
          {reparability === null ? null : (
            <FaultReparabilityChartTooltip
              showBadge
              reparability={reparability}
              reparabilityActions={[]}
              translations={translations}
            />
          )}
          {flags ? <FlagChartTooltip flags={flags} /> : null}
        </ChartTooltip>,
      );
    },

    [project.timezone, translations],
  );

  return React.useMemo(() => {
    const _: Options = {
      initial: {
        xAxis: initialXAxisOptions,
        yAxis: [initialYAxisOptions],
      },
      chart: {
        zoomType: "xy",
        marginTop: CHART_SELECTION_PLOT_LINE_MARGIN,
      },
      series,
      xAxis: {
        breaks: xBreaks,
      },
      yAxis: [
        {
          id: yAxisId,
          visible: true,
          labels: {
            sign: false,
            enabled: true,
            unit: qoi?.unit ?? "",
          },
        },
        FLAG_AXIS,
      ],
      tooltip: {
        sticky: true,
        formatter: constantTooltipFormatter,
      },
      navigator: {
        enabled: true,
        xAxis: {
          breaks: xBreaks,
          /**
           * In this chart, it is possible that published active analyses interval is
           * less than the interval of active analyses. Navigator try to stick to available
           * values, so we need to set min and max to the active analyses interval
           * to preserve visual consistency.
           */
          min:
            initialXAxisOptions.initialUserMin === null
              ? undefined
              : Math.min(initialXAxisOptions.initialUserMin, analysisDateMin),
          max: Date.now(),
        },
      },
    };
    return _;
  }, [
    initialXAxisOptions,
    initialYAxisOptions,
    series,
    xBreaks,
    yAxisId,
    qoi?.unit,
    constantTooltipFormatter,
    analysisDateMin,
  ]);
};

export default useOptions;
